import React from 'react'

import * as constants from '../../static/constants'
import styled from 'styled-components'
import { Button, Counter } from 'components/atoms'
import { ISelectedGuests } from 'utils/reservation'
import FlashMessage from 'components/organisms/FlashMessage'

interface IWindow {
  flashMessages: FlashMessage
}
declare var window: IWindow

interface IMultiDatePickerProps {
  isPrivate: boolean
  guests: ISelectedGuests
  align?: 'left' | 'right'
  relative?: boolean
  departure?: {
    available_count: number
    limit_people: number
  }
  autoApply?: boolean
  setGuests:
    | React.Dispatch<React.SetStateAction<ISelectedGuests>>
    | ((prevState: ISelectedGuests) => void)
  setIsOpenGuestCount?: (isOpen: boolean) => void
}

export const MinGuestsCounter: React.VFC<IMultiDatePickerProps> = ({
  guests,
  isPrivate,
  align = 'right',
  relative = false,
  autoApply = false,
  setGuests,
  setIsOpenGuestCount,
}) => {
  const [selectedGuests, setSelectedGuests] = React.useState<ISelectedGuests>({
    adults: guests.adults,
    children: guests.children ?? 0,
    infants: guests.infants ?? 0,
  })

  const applyGuests = React.useCallback(() => {
    const updatedGuests: ISelectedGuests = { ...selectedGuests }

    const hasAnyGuests =
      updatedGuests.adults > 0 || updatedGuests.children > 0 || updatedGuests.infants > 0
    if (!hasAnyGuests) {
      updatedGuests.adults = 1
      window.flashMessages.addMessage({
        text: 'Please select at least one guest.',
        type: 'error',
      })
    }
    const isInfantsWithoutAdults = updatedGuests.infants > 0 && updatedGuests.adults === 0
    if (isInfantsWithoutAdults) {
      updatedGuests.adults = 1
      window.flashMessages.addMessage({
        text: 'Please select at least one adult.',
        type: 'error',
      })
    }
    setGuests(updatedGuests)
    if (setIsOpenGuestCount) {
      setIsOpenGuestCount(false)
    }
  }, [selectedGuests])

  React.useEffect(() => {
    if (autoApply) {
      setGuests(selectedGuests)
    }
  }, [autoApply, selectedGuests])

  return (
    <GuestsCountWrapper align={align} relative={relative}>
      <div className="GuestCountContainer">
        <div className="GuestCount">
          <div className="GuestCountLabel">
            {isPrivate ? (
              <>
                <p className="label">Guests</p>
              </>
            ) : (
              <>
                <p className="label">Adults</p>
              </>
            )}
          </div>
          <Counter
            defaultValue={guests.adults}
            minValue={1}
            handleChange={count => {
              setSelectedGuests(old => ({ ...old, adults: count }))
            }}
            changeableCountLabel={true}
          />
        </div>
        {!isPrivate && (
          <div className="GuestCount">
            <div className="GuestCountLabel">
              <p className="label">Children</p>
            </div>
            <Counter
              defaultValue={guests.children}
              handleChange={count => {
                setSelectedGuests(old => ({ ...old, children: count }))
              }}
              changeableCountLabel={true}
            />
          </div>
        )}
        {!isPrivate && (
          <div className="GuestCount">
            <div className="GuestCountLabel">
              <p className="label">Infants</p>
              {/* {(ages.infant.max || ages.child.min) && (
                <p className="age">Under {ages.infant.max ?? ages.child.min}</p>
              )} */}
            </div>
            <Counter
              defaultValue={guests.infants}
              handleChange={count => {
                setSelectedGuests(old => ({ ...old, infants: count }))
              }}
              changeableCountLabel={true}
            />
          </div>
        )}
      </div>
      <div className="Buttons">
        <Button
          handleClick={() => {
            setSelectedGuests(constants.DEFAULT_SEARCH_FILTER_GUESTS)
            setGuests(constants.DEFAULT_SEARCH_FILTER_GUESTS)
            if (setIsOpenGuestCount) {
              setIsOpenGuestCount(false)
            }
          }}
          large={true}
          textColor={constants.COLORS.subText}
          borderColor={constants.COLORS.divider}
        >
          Reset
        </Button>
        {!autoApply && (
          <Button handleClick={applyGuests} primary={true} large={true}>
            Save
          </Button>
        )}
      </div>
    </GuestsCountWrapper>
  )
}

const GuestsCountWrapper = styled.div`
  position: ${({ relative }) => (relative ? 'relative' : 'absolute')};
  width: 100%;
  max-width:${({ relative }) => (relative ? 'unset' : '328px')};
  z-index: 10;
  background-color: ${constants.COLORS.white};
  border-radius: 16px;
  box-shadow:  ${({ relative }) => (relative ? 'none' : constants.COLORS.cardShadow)};
  padding: 24px;
  left: ${({ align }) => (align === 'left' ? 0 : 'unset')};
  right: ${({ align }) => (align === 'right' ? 0 : 'unset')};
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    margin-left: 16px;
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    margin: ${({ relative }) => (relative ? '0 auto' : 0)};
    > div {
      margin: 0 auto;
    }
  }
  .GroupTourNote {
    margin-bottom: 1.6rem;
    p:nth-child(2) {
      margin-top: 0.4rem;
    }
    .SplitNote {
      font-size: 1.1rem;
    }
  }
  .GuestCount {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 1.6rem;
    margin-bottom: 1.6rem;
    border-top: 1px solid ${constants.COLORS.divider2};
    &:first-child {
      border: 0;
      padding-top: 0;
    }
    .number {
      font-size: 1.6rem;
    }
    .label {
      line-height: 2.4rem
      font-size: 1.6rem;
    }
    .age {
      line-height: 1.6rem
      font-size: 1.1rem;
      color: ${constants.COLORS.subText};
    }
  }
  .GuestCountContainer {
    padding: 1.6rem;
  }
  .Buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-top: 0.4rem;
    padding: 0.4rem 2rem 1.6rem 0;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 0.8rem 0 !important;
    }
    button {
      width: 11rem;
      & + button {
        margin-left: 1.6rem;
      }
    }
  }
`
